import React, { useContext, useMemo } from 'react'

import {
  GreetingConditionTypeEnum,
  GreetingsTableGate,
  greetingIdsSelected,
  greetingsStatus,
  greetingsTableQuery,
} from '@/entities/greetings'
import {
  type ChangeGreetingPriorityModel,
  type GreetingModel,
} from '@/shared/api'
import { parseDateStringToDayjsDate } from '@/shared/lib/form-utils'
import { COMMON_COLUMN_WIDTH } from '@/shared/lib/styling'
import { EmptyContent, HidingSpin, TableDragHandle } from '@/shared/ui'
import { greetingFormModalOpened } from '@/widgets/GreetingForm'
import { CloseOutlined, EditOutlined } from '@ant-design/icons'
import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button, Space, Table } from 'antd'
import type { TableColumnsType } from 'antd'
import { type TableProps } from 'antd/lib'
import { useUnit } from 'effector-react'

import { greetingsOrder } from '../../api'
import { COLUMN_WIDTH } from '../../lib/column-width'
import {
  $greetingIdsOrder,
  $greetingsReorderBlocked,
  $greetingsReordered,
  greetingIdsOrderSet,
} from '../../model/settings-greetings-active-tab.model'

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  listeners?: SyntheticListenerMap
}

const RowContext = React.createContext<RowContextProps>({})

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext)
  return (
    <Button
      type="text"
      icon={<TableDragHandle />}
      style={{
        cursor: 'move',
        backgroundColor: 'transparent',
        boxShadow: 'none',
      }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  )
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string
}

const Row: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] })

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  )

  return (
    <RowContext.Provider value={contextValue}>
      <tr
        {...props}
        ref={setNodeRef}
        style={{
          ...props.style,
          transform: CSS.Translate.toString(transform),
          transition,
          ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
        }}
        {...attributes}
      />
    </RowContext.Provider>
  )
}

export const ActiveGreetingsTab = () => {
  const openFormModal = useUnit(greetingFormModalOpened)

  const changeStatus = useUnit(greetingsStatus)
  const handleEdit = (id: string) => {
    openFormModal({ id })
  }

  const handleDeactivate = (id: string) => {
    changeStatus.start({
      data: {
        greetingIds: [id],
        isActive: false,
      },
    })
  }

  const { data, error } = useUnit(greetingsTableQuery)

  const orderIds = useUnit($greetingIdsOrder)
  const setOrderIds = useUnit(greetingIdsOrderSet)
  const orderedData = useUnit($greetingsReordered)

  const greetingsReorderBlocked = useUnit($greetingsReorderBlocked)
  const changeSelectedIds = useUnit(greetingIdsSelected)
  const updateOrder = useUnit(greetingsOrder)

  const columns: TableColumnsType<GreetingModel> = [
    {
      key: 'sort',
      align: 'center',
      width: COLUMN_WIDTH.DRAG,
      render: () => <DragHandle />,
      onCell: () => ({
        style: {
          backgroundColor: '#fafafa',
          pointerEvents: greetingsReorderBlocked ? 'none' : 'auto',
          opacity: greetingsReorderBlocked ? 0.3 : 1,
        },
      }),
    },
    {
      title: 'Наименование',
      ellipsis: true,
      dataIndex: 'name',
      key: 'name',
      render: (name, _) => name,
    },
    {
      title: 'Дата',
      width: COLUMN_WIDTH.DATE,
      dataIndex: 'date',
      key: 'date',
      render: (_, greeting) =>
        greeting.conditionType === GreetingConditionTypeEnum.EveryDay
          ? 'Ежедневно'
          : greeting.sendOnStartDate
            ? `${parseDateStringToDayjsDate(greeting.sendOnStartDate).format('DD-MM')}${greeting.sendOnEndDate ? ` - ${parseDateStringToDayjsDate(greeting.sendOnEndDate).format('DD-MM')}` : ''}`
            : '',
    },
    {
      title: '',
      key: 'actions',
      width: COMMON_COLUMN_WIDTH.ACTIONS,
      align: 'center',
      render: (_, greeting) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(greeting.id)}
          />
          <Button
            onClick={() => handleDeactivate(greeting.id)}
            icon={<CloseOutlined style={{ color: 'red' }} />}
          />
        </Space>
      ),
    },
  ]

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = orderIds.findIndex((id) => id === active?.id)
      const overIndex = orderIds.findIndex((id) => id === over?.id)
      const newOrderIds = arrayMove(orderIds, activeIndex, overIndex)
      setOrderIds(newOrderIds)

      const data: ChangeGreetingPriorityModel[] = newOrderIds.map((id, i) => {
        return {
          id,
          priority: i + 1,
        }
      })
      updateOrder.start({ data })
    }
  }

  // Selection

  const rowSelection: TableProps<GreetingModel>['rowSelection'] = {
    onChange: (_, selectedRows: GreetingModel[]) => {
      const ids = selectedRows.map((row) => row.id)
      changeSelectedIds(ids)
    },
    getCheckboxProps: (record: GreetingModel) => ({
      name: record.name,
    }),
  }

  return (
    <>
      <GreetingsTableGate initialFilters={{ IsActive: true }} />

      <HidingSpin spinning={!data?.items && !error}>
        {data?.items && data.items.length > 0 ? (
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={orderedData.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <Table<GreetingModel>
                sticky
                showSorterTooltip={false}
                rowKey="id"
                components={{ body: { row: Row } }}
                rowSelection={{ type: 'checkbox', ...rowSelection }}
                columns={columns}
                dataSource={orderedData}
                pagination={false}
              />
            </SortableContext>
          </DndContext>
        ) : (
          <EmptyContent description="В списке пока нет ни одного приветственного экрана" />
        )}
      </HidingSpin>
    </>
  )
}
